import * as XLSX from 'xlsx'
import { formatDateRu } from './analyticsCalculations.js'

/**
 * Sanitizes user-entered text to mitigate CSV / Excel formula injection.
 * Any string beginning with '=', '+', '-', '@' is prefixed with an apostrophe.
 */
export function sanitizeExcelText(val) {
  if (val === null || val === undefined) return ''
  const str = String(val)
  if (str.startsWith('=') || str.startsWith('+') || str.startsWith('-') || str.startsWith('@')) {
    return `'${str}`
  }
  return str
}

/**
 * Helper to build money-formatted cell for Excel and Google Sheets.
 * Number is stored as numeric value for calculations, with ruble symbol format.
 */
export function moneyCell(val) {
  const num = Math.round(Number(val) || 0)
  return { t: 'n', v: num, z: '#,##0\\ "₽"' }
}

/**
 * Helper to build percentage-formatted cell.
 */
export function percentCell(pct) {
  const num = (Number(pct) || 0) / 100
  return { t: 'n', v: num, z: '0.0%' }
}

/**
 * Helper for formula-calculated money cell.
 */
export function formulaMoneyCell(formula, fallbackVal) {
  const num = Math.round(Number(fallbackVal) || 0)
  return { t: 'n', f: formula, v: num, z: '#,##0\\ "₽"' }
}

/**
 * Helper for integer/quantity formula cell.
 */
export function formulaNumberCell(formula, fallbackVal) {
  const num = Number(fallbackVal) || 0
  return { t: 'n', f: formula, v: num, z: '#,##0' }
}

/**
 * Computes dynamic column widths to prevent ### clipping in Excel.
 */
function setAutoWidths(ws, aoa, minWidths = []) {
  const colWidths = []
  aoa.forEach(row => {
    if (!Array.isArray(row)) return
    row.forEach((cell, cIdx) => {
      let len = 0
      if (cell && typeof cell === 'object') {
        if (cell.v !== undefined) {
          len = String(cell.v).length + (cell.z ? 4 : 0)
        }
      } else if (cell !== null && cell !== undefined) {
        len = String(cell).length
      }
      colWidths[cIdx] = Math.max(colWidths[cIdx] || minWidths[cIdx] || 12, len + 3)
    })
  })
  ws['!cols'] = colWidths.map(w => ({ wch: Math.min(Math.max(w, 14), 52) }))
}

/**
 * Builds the multi-sheet XLSX Workbook instance.
 * Separated from file writing to allow direct programmatic validation in tests.
 */
export function buildExcelWorkbook({ metrics, period, activePvz, organizationName, rawData = {} }) {
  const wb = XLSX.utils.book_new()
  const pvzName = sanitizeExcelText(activePvz?.name || 'ПВЗ')
  const orgName = sanitizeExcelText(organizationName || activePvz?.organizationName || 'Организация')
  const dateFrom = formatDateRu(period.startDate)
  const dateTo = formatDateRu(period.endDate)
  const generatedAt = new Date().toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

  // ==========================================
  // 1. ЛИСТ: «Сводка»
  // ==========================================
  const summaryAoa = [
    ['ПВЗ РАСЧЁТ — ФИНАНСОВЫЙ АНАЛИТИЧЕСКИЙ ОТЧЁТ'],
    ['Организация:', orgName],
    ['Пункт выдачи (ПВЗ):', pvzName],
    ['Период отчёта:', `${dateFrom} — ${dateTo}`],
    ['Сформирован:', generatedAt],
    [], // Пустая строка-разделитель
    ['Показатель', 'Сумма', 'Доля / Рентабельность']
  ]

  // Доходы
  summaryAoa.push(['Выручка (Доходы всего)', moneyCell(metrics.totalIncome), percentCell(100)])
  summaryAoa.push([
    '  — в т.ч. Выручка Wildberries',
    moneyCell(metrics.totalWbIncome),
    percentCell(metrics.totalIncome > 0 ? (metrics.totalWbIncome / metrics.totalIncome) * 100 : 0)
  ])
  if (metrics.totalOtherIncome > 0) {
    summaryAoa.push([
      '  — в т.ч. Прочие доходы',
      moneyCell(metrics.totalOtherIncome),
      percentCell(metrics.totalIncome > 0 ? (metrics.totalOtherIncome / metrics.totalIncome) * 100 : 0)
    ])
  }

  // Расходы
  summaryAoa.push([
    'Расходы (Всего)',
    moneyCell(metrics.totalExpensesAll),
    percentCell(metrics.totalIncome > 0 ? (metrics.totalExpensesAll / metrics.totalIncome) * 100 : 0)
  ])
  summaryAoa.push(['  — ФОТ (Зарплаты сотрудников)', moneyCell(metrics.payrollTotal), percentCell(metrics.pctPayroll)])
  summaryAoa.push(['  — Постоянные расходы (Аренда, интернет и др.)', moneyCell(metrics.fixedPaidExpenses), percentCell(metrics.pctFixed)])
  summaryAoa.push(['  — Переменные расходы (Расходники, хозтовары)', moneyCell(metrics.variableExpenses), percentCell(metrics.pctVar)])
  summaryAoa.push(['  — Налог УСН (6%)', moneyCell(metrics.taxAmount), percentCell(metrics.pctTax)])
  if (metrics.totalPartnerCommission > 0) {
    summaryAoa.push(['  — Расходы на партнёров (комиссии)', moneyCell(metrics.totalPartnerCommission), percentCell(metrics.pctPartner)])
  }

  // Финансовый результат (Четкие, прозрачные термины)
  summaryAoa.push([
    'Чистая прибыль (Операционный результат)',
    moneyCell(metrics.actualFreeCashFlow),
    percentCell(metrics.profitMarginPct)
  ])
  summaryAoa.push([
    'EBITDA (Прибыль до налога УСН)',
    moneyCell(metrics.actualFreeCashFlow + metrics.taxAmount),
    percentCell(metrics.totalIncome > 0 ? ((metrics.actualFreeCashFlow + metrics.taxAmount) / metrics.totalIncome) * 100 : 0)
  ])

  // Справочно: резервные фонды
  if (metrics.reservesTotal > 0 || (metrics.actualReserves && metrics.actualReserves > 0)) {
    const resAmt = metrics.reservesTotal || metrics.actualReserves || 0
    summaryAoa.push(['  — Справочно: Отчисления в резервные фонды', moneyCell(resAmt), ''])
    summaryAoa.push(['Свободный остаток после резервов', moneyCell(metrics.actualFreeCashFlow - resAmt), ''])
  }

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa)
  setAutoWidths(wsSummary, summaryAoa, [48, 22, 26])
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Сводка')

  // ==========================================
  // 2. ЛИСТ: «Доходы» (если есть поступления)
  // ==========================================
  const displayedIncomes = metrics.displayedIncomes || []
  if (displayedIncomes.length > 0) {
    const incomesAoa = [
      ['Дата / Период', 'Источник', 'Сумма к выплате', 'Оборот', 'Премия', 'Удержания', 'Компенсации']
    ]

    let sumAmount = 0
    let sumTurnover = 0
    let sumBonus = 0
    let sumPenalty = 0
    let sumComp = 0

    displayedIncomes.forEach(i => {
      const periodLabel = (i.period_start && i.period_end)
        ? `${formatDateRu(i.period_start)} — ${formatDateRu(i.period_end)}`
        : formatDateRu(i.date)
      const amt = Number(i.amount || 0)
      const to = Number(i.turnover || 0)
      const bns = Number(i.bonus || 0)
      const pen = Number(i.penalty || 0)
      const cmp = Number(i.compensation || 0)

      sumAmount += amt
      sumTurnover += to
      sumBonus += bns
      sumPenalty += pen
      sumComp += cmp

      incomesAoa.push([
        periodLabel,
        sanitizeExcelText(i.source || 'Wildberries'),
        moneyCell(amt),
        moneyCell(to),
        moneyCell(bns),
        moneyCell(pen),
        moneyCell(cmp)
      ])
    })

    const n = displayedIncomes.length
    incomesAoa.push([
      'ИТОГО',
      '',
      formulaMoneyCell(`SUM(C2:C${n + 1})`, sumAmount),
      formulaMoneyCell(`SUM(D2:D${n + 1})`, sumTurnover),
      formulaMoneyCell(`SUM(E2:E${n + 1})`, sumBonus),
      formulaMoneyCell(`SUM(F2:F${n + 1})`, sumPenalty),
      formulaMoneyCell(`SUM(G2:G${n + 1})`, sumComp)
    ])

    const wsIncomes = XLSX.utils.aoa_to_sheet(incomesAoa)
    setAutoWidths(wsIncomes, incomesAoa, [26, 18, 20, 18, 16, 16, 18])
    wsIncomes['!autofilter'] = { ref: `A1:G${n + 1}` }
    XLSX.utils.book_append_sheet(wb, wsIncomes, 'Доходы')
  }

  // ==========================================
  // 3. ЛИСТ: «Расходы» (если есть расходы)
  // ==========================================
  const periodExpenses = metrics.selectedPeriodExpenses || []
  if (periodExpenses.length > 0) {
    const expensesAoa = [
      ['Дата', 'Категория', 'Описание', 'Тип расхода', 'Сумма', 'Статус оплаты']
    ]

    let sumExp = 0
    periodExpenses.forEach(e => {
      const amt = Number(e.amount || 0)
      sumExp += amt
      const typeLabel = e.type === 'fixed_payment' ? 'Постоянный' : 'Переменный'
      const statusLabel = e.is_paid !== false ? 'Оплачен' : 'Запланирован'

      expensesAoa.push([
        formatDateRu(e.date),
        sanitizeExcelText(e.category || 'Прочее'),
        sanitizeExcelText(e.description || e.comment || ''),
        typeLabel,
        moneyCell(amt),
        statusLabel
      ])
    })

    const n = periodExpenses.length
    expensesAoa.push([
      'ИТОГО',
      '',
      '',
      '',
      formulaMoneyCell(`SUM(E2:E${n + 1})`, sumExp),
      ''
    ])

    const wsExpenses = XLSX.utils.aoa_to_sheet(expensesAoa)
    setAutoWidths(wsExpenses, expensesAoa, [14, 24, 34, 16, 20, 16])
    wsExpenses['!autofilter'] = { ref: `A1:F${n + 1}` }
    XLSX.utils.book_append_sheet(wb, wsExpenses, 'Расходы')
  }

  // ==========================================
  // 4. ЛИСТ: «Зарплата» (если есть сотрудники)
  // ==========================================
  const salaryList = metrics.salaryList || []
  if (salaryList.length > 0) {
    const salaryAoa = [
      ['Сотрудник', 'Должность', 'Смен отработано', 'Часов отработано', 'Оклад / База', 'Премии / Бонусы', 'Штрафы / Удержания', 'Итого к выплате']
    ]

    let sumShifts = 0
    let sumHours = 0
    let sumBase = 0
    let sumBonus = 0
    let sumDeductions = 0
    let sumTotal = 0

    salaryList.forEach(s => {
      const shifts = Number(s.shiftsCount || s.daysWorked || 0)
      const hours = Number(s.workHours || s.hoursWorked || 0)
      const base = Number(s.baseAmount || s.base || 0)
      const bns = Number(s.bonusAmount || s.bonuses || 0)
      const ded = Number(s.deductionsAmount || s.deductions || 0)
      const tot = Number(s.total || 0)

      sumShifts += shifts
      sumHours += hours
      sumBase += base
      sumBonus += bns
      sumDeductions += ded
      sumTotal += Math.max(tot, 0)

      salaryAoa.push([
        sanitizeExcelText(s.employeeName || 'Сотрудник'),
        sanitizeExcelText(s.role || 'Оператор ПВЗ'),
        shifts,
        hours,
        moneyCell(base),
        moneyCell(bns),
        moneyCell(ded),
        moneyCell(tot)
      ])
    })

    const n = salaryList.length
    salaryAoa.push([
      'ИТОГО',
      '',
      formulaNumberCell(`SUM(C2:C${n + 1})`, sumShifts),
      formulaNumberCell(`SUM(D2:D${n + 1})`, sumHours),
      formulaMoneyCell(`SUM(E2:E${n + 1})`, sumBase),
      formulaMoneyCell(`SUM(F2:F${n + 1})`, sumBonus),
      formulaMoneyCell(`SUM(G2:G${n + 1})`, sumDeductions),
      formulaMoneyCell(`SUM(H2:H${n + 1})`, sumTotal)
    ])

    const wsSalary = XLSX.utils.aoa_to_sheet(salaryAoa)
    setAutoWidths(wsSalary, salaryAoa, [28, 20, 18, 18, 20, 20, 22, 22])
    wsSalary['!autofilter'] = { ref: `A1:H${n + 1}` }
    XLSX.utils.book_append_sheet(wb, wsSalary, 'Зарплата')
  }

  // ==========================================
  // 5. ЛИСТ: «Динамика по дням» (Строгая сходимость со «Сводкой»)
  // ==========================================
  const flowChart = metrics.flowChart || []
  if (flowChart.length > 0) {
    const flowAoa = [
      ['Дата', 'День недели', 'Доход', 'Расход', 'Чистая прибыль']
    ]

    let totInc = 0
    let totExp = 0
    let totPrf = 0

    flowChart.forEach(f => {
      const inc = Number(f.income || 0)
      const exp = Number(f.expense || 0)
      const prf = Number(f.realProfit !== undefined ? f.realProfit : (inc - exp))

      totInc += inc
      totExp += exp
      totPrf += prf

      const dayOfWeek = (f.displayDate || '').split(' ')[1] || ''

      flowAoa.push([
        formatDateRu(f.date),
        dayOfWeek,
        moneyCell(inc),
        moneyCell(exp),
        moneyCell(prf)
      ])
    })

    const n = flowChart.length
    flowAoa.push([
      'ИТОГО',
      '',
      formulaMoneyCell(`SUM(C2:C${n + 1})`, totInc),
      formulaMoneyCell(`SUM(D2:D${n + 1})`, totExp),
      formulaMoneyCell(`SUM(E2:E${n + 1})`, totPrf)
    ])

    const wsFlow = XLSX.utils.aoa_to_sheet(flowAoa)
    setAutoWidths(wsFlow, flowAoa, [16, 14, 20, 20, 22])
    wsFlow['!autofilter'] = { ref: `A1:E${n + 1}` }
    XLSX.utils.book_append_sheet(wb, wsFlow, 'Динамика по дням')
  }

  // ==========================================
  // 6. ЛИСТ: «Партнёры» (если есть)
  // ==========================================
  const partnerTurnovers = metrics.selectedPeriodPartnerTurnovers || []
  if (partnerTurnovers.length > 0) {
    const ptAoa = [
      ['Дата', 'ID Партнёра', 'Оборот партнёра', 'Комиссия партнёра']
    ]

    let totTurnover = 0
    let totComm = 0

    partnerTurnovers.forEach(t => {
      const to = Number(t.turnover_amount || 0)
      const comm = Number(t.commission_amount || 0)
      totTurnover += to
      totComm += comm

      ptAoa.push([
        formatDateRu(t.entry_date),
        sanitizeExcelText(t.partner_id || 'Партнёр'),
        moneyCell(to),
        moneyCell(comm)
      ])
    })

    const n = partnerTurnovers.length
    ptAoa.push([
      'ИТОГО',
      '',
      formulaMoneyCell(`SUM(C2:C${n + 1})`, totTurnover),
      formulaMoneyCell(`SUM(D2:D${n + 1})`, totComm)
    ])

    const wsPt = XLSX.utils.aoa_to_sheet(ptAoa)
    setAutoWidths(wsPt, ptAoa, [16, 36, 22, 24])
    wsPt['!autofilter'] = { ref: `A1:D${n + 1}` }
    XLSX.utils.book_append_sheet(wb, wsPt, 'Партнёры')
  }

  return wb
}

/**
 * Generates and triggers download of the Excel workbook.
 */
export function exportAnalyticsToExcel(params) {
  const wb = buildExcelWorkbook(params)
  const fileName = `PVZ_Raschet_Analytics_${params.period.startDate}_${params.period.endDate}.xlsx`
  XLSX.writeFile(wb, fileName)
  return fileName
}
